import React from "react";
import { splitBullets, toBlocks } from "./copyBlocks";

/*
 * The process copy is an ordered sequence in every locale — "Step 1: Reach Out"
 * in English, "第1步：联系我们" in both Chinese locales. Each puts a digit inside a
 * short prefix closed by a colon, so match that shape rather than either
 * locale's wording: the ordinal moves into the list marker and must not be
 * repeated in the visible title.
 *
 * Blocks without a step prefix stay prose — carrier screening closes its
 * process with a plain paragraph — and render after the list rather than
 * being numbered as a step.
 */
const STEP_PREFIX = /^[^:：]{0,12}\d+[^:：]{0,4}[:：]\s*/;

// Step bodies carry bullets of their own: carrier screening's last step lists
// the reproductive options under it. They get the same list treatment as
// bullets anywhere else in the copy rather than being left as literal "•" text.
const Lines = ({ lines }) => {
  const { prose, bullets } = splitBullets(lines);
  return (
    <>
      {prose.map((line, lineIndex) => (
        <p key={lineIndex}>{line}</p>
      ))}
      {bullets.length > 0 && (
        <ul className="service-prose-list">
          {bullets.map((bullet, bulletIndex) => (
            <li key={bulletIndex}>{bullet}</li>
          ))}
        </ul>
      )}
    </>
  );
};

const renderProcessSteps = (text) => {
  if (!text) return null;

  const blocks = toBlocks(text);

  if (blocks.length === 0) return null;

  const nodes = [];
  let steps = [];

  const flushSteps = () => {
    if (steps.length === 0) return;
    const stepped = steps;
    steps = [];
    nodes.push(
      // The redundant-role lint is correct in general, but not here: the
      // custom step marker needs `list-style: none`, and Safari drops list
      // semantics from a list styled that way, so VoiceOver stops announcing
      // the count and position. Restating the role restores them.
      // eslint-disable-next-line jsx-a11y/no-redundant-roles
      <ol className="service-steps" role="list" key={`steps-${nodes.length}`}>
        {stepped.map((step, stepIndex) => (
          <li className="service-step" key={stepIndex}>
            <span className="service-step-marker" aria-hidden="true">
              {stepIndex + 1}
            </span>
            <div className="service-step-body">
              <h3 className="service-step-title">{step.title}</h3>
              <Lines lines={step.body} />
            </div>
          </li>
        ))}
      </ol>
    );
  };

  blocks.forEach((lines, blockIndex) => {
    const [first, ...rest] = lines;

    if (STEP_PREFIX.test(first)) {
      steps.push({ title: first.replace(STEP_PREFIX, ""), body: rest });
      return;
    }

    flushSteps();
    nodes.push(
      <div className="service-prose" key={`prose-${blockIndex}`}>
        <Lines lines={lines} />
      </div>
    );
  });

  flushSteps();

  return <div className="service-process">{nodes}</div>;
};

export default renderProcessSteps;
